import React, { useState } from 'react';
import { Button, Select, Spin, Tabs, Tooltip, Upload } from 'antd';
import { InfoCircleOutlined, LeftOutlined, RightOutlined, StarOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import {
  FloatingLabelInput,
  FloatingLabelSelect,
} from '../elements/floatingLabelInput/FloatingLabelInput';
import ToggleSwitch from '../elements/toggleSwitch/ToggleSwitch';
import { PRODUCT_FLAG_GROUPS, PRODUCT_FLAG_TOOLTIPS } from '../../constants/product-constants';
import RichTextEditor from '../elements/RichTextEditor/RichTextEditor';
import TranslateButton from '../elements/TranslateButton/TranslateButton';
import { useTranslateFields } from '../../hooks/useTranslateFields';
import styles from '../../styles/mobileInfoPage.module.scss';
import uploadStyles from '../../styles/editAnnoucement.module.scss';
import floatingStyles from '../elements/floatingLabelInput/floatingLabelInput.module.scss';

const LANG_SECTIONS = [
  { key: 'sr', label: 'Srpski' },
  { key: 'hu', label: 'Mađarski' },
  { key: 'en', label: 'Engleski' },
];

const STATUS_OPTIONS = [
  { value: 'publish', label: 'Objavljen' },
  { value: 'draft', label: 'Nacrt' },
];

const CASHBACK_MODE_OPTIONS = [
  { value: 'points', label: 'Poeni' },
  { value: 'deduct_from_total', label: 'Odbitak od ukupnog iznosa (checkout)' },
];

const emptyLangContent = {
  name: '',
  description: '',
  short_description: '',
  helpsWithMetaDataSection: '',
  customFieldMetaDataSection: { title: '', htmlContent: '' },
};

export const EMPTY_PRODUCT_FORM = {
  slug: '',
  sku: '',
  price: '',
  regular_price: '',
  sale_price: '',
  vip_price: '',
  menu_order: '',
  status: 'publish',
  isConfigurationProduct: false,
  isMix4IndividuallySellable: false,
  hasQuantityDiscount: false,
  isEligibleToBuyWithPoints: true,
  isProductHidden: false,
  isExternalProduct: false,
  isSubscriptionOnlyProduct: false,
  isFrozenProduct: false,
  skipReferredDiscount: false,
  categories: [],
  images: [],
  multiLanguage: {
    sr: { ...emptyLangContent },
    hu: { ...emptyLangContent },
    en: { ...emptyLangContent },
  },
  mentorCommissionChain: {
    enabled: false,
    maxPercent: '',
    level1Percent: '',
    level2Percent: '',
    level3Percent: '',
    level4Percent: '',
  },
  agentCommission: {
    enabled: false,
    percent: '',
  },
  checkoutUpsell: {
    enabled: false,
    discountPercent: '',
  },
  cashbackOverride: {
    hasOverride: false,
    enabled: true,
    mode: 'points',
    percent: '',
  },
};

// Order-eligibility gates (not pricing rules) that reject the whole order outright,
// unrelated to whether a line was bought via the accepted upsell offer — a checkout-
// upsell product carrying one of these would fail at checkout almost every time. Must
// stay in sync with ProductService.checkoutUpsellIneligibleFlags on the backend
// (product.service.ts). Other flags (hasQuantityDiscount, isExternalProduct,
// isFrozenProduct) may freely coexist with the checkout-upsell flag — the backend fully
// overrides them for an accepted upsell line, they still apply normally otherwise.
const CHECKOUT_UPSELL_INELIGIBLE_FLAGS = [
  { key: 'isConfigurationProduct', label: 'konfiguracioni proizvod' },
  { key: 'isSubscriptionOnlyProduct', label: 'pretplatnički paket (3/6/12)' },
];

const COMMISSION_CHAIN_LEVELS = [
  { key: 'level1Percent', label: 'Nivo 1 (%)' },
  { key: 'level2Percent', label: 'Nivo 2 (%)' },
  { key: 'level3Percent', label: 'Nivo 3 (%)' },
  { key: 'level4Percent', label: 'Nivo 4 (%)' },
];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const LANGS = ['sr', 'hu', 'en'];
const LANG_LABELS = { sr: 'srpskom', hu: 'mađarskom', en: 'engleskom' };

// Validates the form, toasting the first problem found. Kept intentionally
// simple/flat (not a rule engine) since the field set is fixed and small.
// Mirrors the backend's validateMultiLanguageContent (src/services/product.service.ts)
// so the same rules are enforced on both sides.
const validate = (formData) => {
  if (!formData.slug?.trim()) {
    toast.error('Slug je obavezan');
    return false;
  }
  if (formData.price === '' || Number.isNaN(Number(formData.price))) {
    toast.error('Cena je obavezna');
    return false;
  }
  if (formData.regular_price === '' || Number.isNaN(Number(formData.regular_price))) {
    toast.error('Redovna cena je obavezna');
    return false;
  }

  for (const lang of LANGS) {
    if (!formData.multiLanguage[lang].name?.trim()) {
      toast.error(`Naziv na ${LANG_LABELS[lang]} je obavezan`);
      return false;
    }
    if (!formData.multiLanguage[lang].short_description?.trim()) {
      toast.error(`Kratak opis na ${LANG_LABELS[lang]} je obavezan`);
      return false;
    }
    if (!formData.multiLanguage[lang].description?.trim()) {
      toast.error(`Opis na ${LANG_LABELS[lang]} je obavezan`);
      return false;
    }
  }

  const consistencyChecks = [
    { get: (l) => formData.multiLanguage[l].helpsWithMetaDataSection, label: 'Pomaže kod' },
    { get: (l) => formData.multiLanguage[l].customFieldMetaDataSection.title, label: 'Naslov dodatnog polja' },
    { get: (l) => formData.multiLanguage[l].customFieldMetaDataSection.htmlContent, label: 'Sadržaj dodatnog polja' },
  ];
  for (const { get, label } of consistencyChecks) {
    const filled = LANGS.filter((l) => get(l)?.trim()).length;
    if (filled > 0 && filled < LANGS.length) {
      toast.error(`"${label}" mora biti popunjeno za sva tri jezika ili ni za jedan`);
      return false;
    }
  }

  if (formData.mentorCommissionChain.enabled) {
    const chain = formData.mentorCommissionChain;
    const fields = ['maxPercent', ...COMMISSION_CHAIN_LEVELS.map((l) => l.key)];
    for (const field of fields) {
      const value = chain[field];
      if (value === '' || Number.isNaN(Number(value)) || Number(value) < 0 || Number(value) > 100) {
        toast.error('Sva polja provizije mentorskog lanca moraju biti brojevi između 0 i 100');
        return false;
      }
    }
    const levelSum = COMMISSION_CHAIN_LEVELS.reduce(
      (sum, { key }) => sum + Number(chain[key]),
      0
    );
    if (levelSum > Number(chain.maxPercent)) {
      toast.error('Zbir procenata po nivoima ne sme biti veći od maksimalnog procenta provizije');
      return false;
    }
  }

  if (formData.agentCommission.enabled) {
    const percent = Number(formData.agentCommission.percent);
    if (formData.agentCommission.percent === '' || Number.isNaN(percent) || percent < 0 || percent > 100) {
      toast.error('Procenat provizije za agenta mora biti broj između 0 i 100');
      return false;
    }
  }

  // Gated on hasOverride, not enabled — the backend requires mode/percent on the
  // override document regardless of its enabled flag (enabled:false means "cashback
  // fully off for this product", a third state distinct from "no override").
  if (formData.cashbackOverride.hasOverride) {
    const { mode, percent } = formData.cashbackOverride;
    if (!CASHBACK_MODE_OPTIONS.some((o) => o.value === mode)) {
      toast.error('Izaberite način cashback-a za ovaj proizvod');
      return false;
    }
    const pct = Number(percent);
    if (percent === '' || Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Procenat cashback-a za ovaj proizvod mora biti broj između 0 i 100');
      return false;
    }
  }

  if (formData.checkoutUpsell.enabled) {
    const percent = Number(formData.checkoutUpsell.discountPercent);
    if (formData.checkoutUpsell.discountPercent === '' || Number.isNaN(percent) || percent <= 0 || percent > 100) {
      toast.error('Popust za ponudu za dodatnu kupovinu mora biti broj između 0 i 100');
      return false;
    }
    const ineligible = CHECKOUT_UPSELL_INELIGIBLE_FLAGS.find(({ key }) => formData[key]);
    if (ineligible) {
      toast.error(`Proizvod ne može biti ponuda za dodatnu kupovinu jer je označen kao "${ineligible.label}"`);
      return false;
    }
  }

  return true;
};

const buildPayload = (formData) => ({
  slug: formData.slug.trim(),
  sku: formData.sku || undefined,
  price: Number(formData.price),
  regular_price: Number(formData.regular_price),
  sale_price: formData.sale_price !== '' ? Number(formData.sale_price) : undefined,
  vip_price: formData.vip_price !== '' ? Number(formData.vip_price) : undefined,
  menu_order: formData.menu_order !== '' ? Number(formData.menu_order) : undefined,
  status: formData.status,
  isConfigurationProduct: formData.isConfigurationProduct,
  isMix4IndividuallySellable: formData.isMix4IndividuallySellable,
  hasQuantityDiscount: formData.hasQuantityDiscount,
  isEligibleToBuyWithPoints: formData.isEligibleToBuyWithPoints,
  isProductHidden: formData.isProductHidden,
  isExternalProduct: formData.isExternalProduct,
  isSubscriptionOnlyProduct: formData.isSubscriptionOnlyProduct,
  isFrozenProduct: formData.isFrozenProduct,
  skipReferredDiscount: formData.skipReferredDiscount,
  categories: formData.categories,
  images: formData.images,
  multiLanguage: formData.multiLanguage,
});

// Returns the commission-chain payload for the separate PUT /products/:id/commission-chain
// call, or null when the override is disabled (caller should DELETE the config instead).
const buildCommissionChainPayload = (formData) => {
  if (!formData.mentorCommissionChain.enabled) return null;
  const chain = formData.mentorCommissionChain;
  return {
    maxPercent: Number(chain.maxPercent),
    level1Percent: Number(chain.level1Percent),
    level2Percent: Number(chain.level2Percent),
    level3Percent: Number(chain.level3Percent),
    level4Percent: Number(chain.level4Percent),
  };
};

// Returns the agent-commission payload for the separate PUT /products/:id/commission-agent
// call, or null when the override is disabled (caller should DELETE the config instead).
const buildAgentCommissionPayload = (formData) => {
  if (!formData.agentCommission.enabled) return null;
  return { percent: Number(formData.agentCommission.percent) };
};

// Returns the cashback-override payload for the separate PUT /products/:id/cashback-override
// call, or null when no override should exist (caller should DELETE instead). Unlike
// mentorCommissionChain/agentCommission, `enabled` here is a real stored flag sent on every
// PUT, not "document exists" — the backend requires mode+percent regardless of its value.
const buildCashbackOverridePayload = (formData) => {
  if (!formData.cashbackOverride.hasOverride) return null;
  return {
    enabled: formData.cashbackOverride.enabled,
    mode: formData.cashbackOverride.mode,
    percent: Number(formData.cashbackOverride.percent),
  };
};

// Payload for the separate PATCH /products/:id/update-checkout-upsell call.
const buildCheckoutUpsellPayload = (formData) => ({
  isCheckoutUpsell: formData.checkoutUpsell.enabled,
  checkoutUpsellDiscountPercent: formData.checkoutUpsell.enabled
    ? Number(formData.checkoutUpsell.discountPercent)
    : undefined,
});

// `initialForm` must already be resolved (non-null) by the time this mounts
// for mode="edit" — the parent page keeps its own loading gate, same as the
// disease/protocol edit pages do before their forms mount.
export default function ProductForm({ mode, initialForm, categoryOptions = [], saving, onSubmit }) {
  const [formData, setFormData] = useState(initialForm || EMPTY_PRODUCT_FORM);
  const [uploadingImage, setUploadingImage] = useState(false);

  // categoryOptions only contains currently-pickable categories (excludes
  // hidden ones and a few reserved names) — a product can still be tagged
  // with a category that's since dropped out of that list (e.g. hidden after
  // assignment). Without its own entry here, antd's Select has no label to
  // resolve for that tag and renders the raw numeric id instead of a name,
  // and any unrelated edit would silently blank its stored name on save.
  const selectableCategoryOptions = [
    ...categoryOptions,
    ...formData.categories
      .filter((c) => !categoryOptions.some((opt) => opt.id === c.id))
      .map((c) => ({ id: c.id, name: c.name })),
  ];

  const setLangField = (lang, field, value) =>
    setFormData((prev) => ({
      ...prev,
      multiLanguage: {
        ...prev.multiLanguage,
        [lang]: { ...prev.multiLanguage[lang], [field]: value },
      },
    }));

  const setLangMetaField = (lang, field, value) =>
    setFormData((prev) => ({
      ...prev,
      multiLanguage: {
        ...prev.multiLanguage,
        [lang]: {
          ...prev.multiLanguage[lang],
          customFieldMetaDataSection: {
            ...prev.multiLanguage[lang].customFieldMetaDataSection,
            [field]: value,
          },
        },
      },
    }));

  const handleImageUpload = async (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Dozvoljen je samo JPG, PNG ili WebP format');
      return false;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Slika je prevelika. Maksimalna veličina je 5MB');
      return false;
    }

    setUploadingImage(true);
    try {
      const base64 = await fileToBase64(file);
      setFormData((prev) => ({
        ...prev,
        images: [
          ...prev.images,
          { id: Date.now() + prev.images.length, src: base64, name: file.name, alt: '' },
        ],
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Greška pri učitavanju slike');
    } finally {
      setUploadingImage(false);
    }
    return false; // prevent antd's default upload behavior
  };

  const removeImage = (id) =>
    setFormData((prev) => ({ ...prev, images: prev.images.filter((img) => img.id !== id) }));

  // images[0] is the product's main image everywhere in the API (shop, cart, orders).
  const moveImage = (fromIndex, toIndex) =>
    setFormData((prev) => {
      const images = [...prev.images];
      const [moved] = images.splice(fromIndex, 1);
      images.splice(toIndex, 0, moved);
      return { ...prev, images };
    });

  const setCommissionChainField = (field, value) =>
    setFormData((prev) => ({
      ...prev,
      mentorCommissionChain: { ...prev.mentorCommissionChain, [field]: value },
    }));

  const setAgentCommissionField = (field, value) =>
    setFormData((prev) => ({
      ...prev,
      agentCommission: { ...prev.agentCommission, [field]: value },
    }));

  const setCheckoutUpsellField = (field, value) =>
    setFormData((prev) => ({
      ...prev,
      checkoutUpsell: { ...prev.checkoutUpsell, [field]: value },
    }));

  const setCashbackOverrideField = (field, value) =>
    setFormData((prev) => ({
      ...prev,
      cashbackOverride: { ...prev.cashbackOverride, [field]: value },
    }));

  const handleSubmit = () => {
    if (!validate(formData)) return;
    onSubmit(
      buildPayload(formData),
      buildCommissionChainPayload(formData),
      buildAgentCommissionPayload(formData),
      buildCheckoutUpsellPayload(formData),
      buildCashbackOverridePayload(formData)
    );
  };

  const { translating, translate } = useTranslateFields();

  const handleTranslate = async () => {
    const sr = formData.multiLanguage.sr;
    if (!sr.name?.trim() || !sr.short_description?.trim() || !sr.description?.trim()) {
      toast.error('Popunite naziv, kratak opis i opis na srpskom pre prevođenja');
      return;
    }

    const fields = {
      name: sr.name,
      short_description: sr.short_description,
      description: sr.description,
    };
    const htmlFields = ['short_description', 'description'];

    if (sr.helpsWithMetaDataSection?.trim()) {
      fields.helpsWithMetaDataSection = sr.helpsWithMetaDataSection;
      htmlFields.push('helpsWithMetaDataSection');
    }
    if (sr.customFieldMetaDataSection.title?.trim()) {
      fields['customFieldMetaDataSection.title'] = sr.customFieldMetaDataSection.title;
      htmlFields.push('customFieldMetaDataSection.title');
    }
    if (sr.customFieldMetaDataSection.htmlContent?.trim()) {
      fields['customFieldMetaDataSection.htmlContent'] = sr.customFieldMetaDataSection.htmlContent;
      htmlFields.push('customFieldMetaDataSection.htmlContent');
    }

    const result = await translate(fields, htmlFields);
    if (!result) return;

    for (const lang of ['hu', 'en']) {
      const translated = result[lang];
      setLangField(lang, 'name', translated.name);
      setLangField(lang, 'short_description', translated.short_description);
      setLangField(lang, 'description', translated.description);
      if (translated.helpsWithMetaDataSection !== undefined) {
        setLangField(lang, 'helpsWithMetaDataSection', translated.helpsWithMetaDataSection);
      }
      if (translated['customFieldMetaDataSection.title'] !== undefined) {
        setLangMetaField(lang, 'title', translated['customFieldMetaDataSection.title']);
      }
      if (translated['customFieldMetaDataSection.htmlContent'] !== undefined) {
        setLangMetaField(lang, 'htmlContent', translated['customFieldMetaDataSection.htmlContent']);
      }
    }

    toast.success('Prevod je uspešno primenjen');
  };

  const langTabItems = LANG_SECTIONS.map(({ key, label }) => ({
    key,
    label,
    children: (
      <div className={styles.fieldStack}>
        <FloatingLabelInput
          label={`Naziv — ${label}`}
          value={formData.multiLanguage[key].name}
          onChange={(e) => setLangField(key, 'name', e.target.value)}
        />
        <div>
          <p className={styles.fieldLabel} style={{ marginBottom: '8px' }}>{`Kratak opis — ${label}`}</p>
          <RichTextEditor
            value={formData.multiLanguage[key].short_description}
            onChange={(value) => setLangField(key, 'short_description', value)}
          />
        </div>
        <div>
          <p className={styles.fieldLabel} style={{ marginBottom: '8px' }}>{`Opis — ${label}`}</p>
          <RichTextEditor
            value={formData.multiLanguage[key].description}
            onChange={(value) => setLangField(key, 'description', value)}
          />
        </div>
        <div>
          <p className={styles.fieldLabel} style={{ marginBottom: '8px' }}>{`Pomaže kod — ${label} (opciono)`}</p>
          <RichTextEditor
            value={formData.multiLanguage[key].helpsWithMetaDataSection}
            onChange={(value) => setLangField(key, 'helpsWithMetaDataSection', value)}
          />
        </div>
        <div>
          <p className={styles.fieldLabel} style={{ marginBottom: '8px' }}>{`Naslov dodatnog polja — ${label} (opciono)`}</p>
          <RichTextEditor
            value={formData.multiLanguage[key].customFieldMetaDataSection.title}
            onChange={(value) => setLangMetaField(key, 'title', value)}
          />
        </div>
        <div>
          <p className={styles.fieldLabel} style={{ marginBottom: '8px' }}>{`Sadržaj dodatnog polja — ${label} (opciono)`}</p>
          <RichTextEditor
            value={formData.multiLanguage[key].customFieldMetaDataSection.htmlContent}
            onChange={(value) => setLangMetaField(key, 'htmlContent', value)}
          />
        </div>
      </div>
    ),
  }));

  return (
    <div>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Osnovni podaci</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '32px' }}>
          <FloatingLabelInput
            label="Slug"
            value={formData.slug}
            onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
          />
          <FloatingLabelInput
            label="SKU (opciono)"
            value={formData.sku}
            onChange={(e) => setFormData((prev) => ({ ...prev, sku: e.target.value }))}
          />
          <FloatingLabelSelect
            label="Status"
            items={STATUS_OPTIONS}
            value={formData.status}
            onChange={(val) => setFormData((prev) => ({ ...prev, status: val }))}
          />
          <FloatingLabelInput
            label="Cena"
            type="number"
            value={formData.price}
            onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))}
          />
          <FloatingLabelInput
            label="Redovna cena"
            type="number"
            value={formData.regular_price}
            onChange={(e) => setFormData((prev) => ({ ...prev, regular_price: e.target.value }))}
          />
          <FloatingLabelInput
            label="Akcijska cena (opciono)"
            type="number"
            value={formData.sale_price}
            onChange={(e) => setFormData((prev) => ({ ...prev, sale_price: e.target.value }))}
          />
          <FloatingLabelInput
            label="VIP cena (opciono)"
            type="number"
            value={formData.vip_price}
            onChange={(e) => setFormData((prev) => ({ ...prev, vip_price: e.target.value }))}
          />
          {/*<FloatingLabelInput
            label="Redosled prikaza (opciono)"
            type="number"
            value={formData.menu_order}
            onChange={(e) => setFormData((prev) => ({ ...prev, menu_order: e.target.value }))}
          />*/}
        </div>
      </section>

      <section className={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className={styles.sectionTitle}>Nazivi i opisi</h3>
          <TranslateButton onClick={handleTranslate} loading={translating} />
        </div>
        <Tabs items={langTabItems} className="lang_tabs" />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Kategorije</h3>
        <div
          className={`${floatingStyles.floatingLabel} ${floatingStyles.multiSelect} ${formData.categories.length > 0 ? floatingStyles.hasValue : ''}`}
        >
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Izaberite kategorije"
            value={formData.categories.map((c) => c.id)}
            options={selectableCategoryOptions.map((c) => ({ value: c.id, label: c.name }))}
            onChange={(selectedIds) =>
              setFormData((prev) => ({
                ...prev,
                categories: selectedIds.map((id) => {
                  const opt = selectableCategoryOptions.find((c) => c.id === id);
                  return { id, name: opt?.name || '' };
                }),
              }))
            }
          />
          <label className={floatingStyles.label}>Kategorije</label>
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Slike</h3>
        {formData.images.length > 1 && (
          <p className={styles.fieldLabel} style={{ marginBottom: '16px' }}>
            Prva slika je glavna — prikazuje se u prodavnici, korpi i porudžbinama.
          </p>
        )}
        <div className={styles.imagesRow}>
          {formData.images.map((img, index) => (
            <div key={img.id} className={uploadStyles.previewWrapper}>
              <img src={img.src} alt={img.alt || img.name} className={uploadStyles.imagePreview} />
              {index === 0 && <span className={uploadStyles.mainBadge}>Glavna slika</span>}
              <div className={uploadStyles.imageActions}>
                {index > 0 && (
                  <Tooltip title="Pomeri levo">
                    <button type="button" aria-label="Pomeri sliku levo" onClick={() => moveImage(index, index - 1)}>
                      <LeftOutlined />
                    </button>
                  </Tooltip>
                )}
                {index > 0 && (
                  <Tooltip title="Postavi kao glavnu">
                    <button type="button" aria-label="Postavi kao glavnu sliku" onClick={() => moveImage(index, 0)}>
                      <StarOutlined />
                    </button>
                  </Tooltip>
                )}
                {index < formData.images.length - 1 && (
                  <Tooltip title="Pomeri desno">
                    <button type="button" aria-label="Pomeri sliku desno" onClick={() => moveImage(index, index + 1)}>
                      <RightOutlined />
                    </button>
                  </Tooltip>
                )}
              </div>
              <button
                className={uploadStyles.deleteBtn}
                onClick={() => removeImage(img.id)}
                type="button"
                aria-label="Ukloni sliku"
              >
                <span className={uploadStyles.trashIcon}>🗑️</span>
              </button>
            </div>
          ))}
          {formData.images.length > 0 ? (
            <Upload
              accept="image/jpeg,image/jpg,image/png,image/webp"
              beforeUpload={handleImageUpload}
              showUploadList={false}
              disabled={uploadingImage}
              multiple
              className={uploadStyles.addImageWrapper}
            >
              <div className={uploadStyles.addImageTile}>
                {uploadingImage ? <Spin /> : <span className={uploadStyles.addImageIcon}>+</span>}
                <span className={uploadStyles.addImageLabel}>
                  {uploadingImage ? 'Učitavanje...' : 'Dodaj još sliku'}
                </span>
                <small className={uploadStyles.addImageHint}>.JPG, .PNG, .WebP</small>
              </div>
            </Upload>
          ) : (
          <Upload
            accept="image/jpeg,image/jpg,image/png,image/webp"
            beforeUpload={handleImageUpload}
            showUploadList={false}
            disabled={uploadingImage}
            multiple
            className={styles.uploadWrapper}
          >
            <div className={uploadStyles.uploadBox}>
              <div className={uploadStyles.uploadContent}>
                <div className={uploadStyles.cloudIconContainer}>
                  {uploadingImage ? <Spin /> : <span className={uploadStyles.cloudIcon}>☁️</span>}
                </div>
                <p className={uploadStyles.uploadText}>
                  <span className={uploadStyles.highlight}>
                    {uploadingImage ? 'Učitavanje...' : 'Kliknite za otpremanje'}
                  </span>{' '}
                  ili prevuci i ispusti
                </p>
                <small className={uploadStyles.fileTypes}>.JPG, .PNG, .WebP (max. 5MB)</small>
              </div>
            </div>
          </Upload>
          )}
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Podešavanja proizvoda</h3>
        <div className={styles.settingsGrid}>
          {PRODUCT_FLAG_GROUPS.map((group) => (
            <div key={group.title}>
              <h4 className={styles.settingsGroupTitle}>{group.title}</h4>
              <div className={styles.settingsCard}>
                {group.flags.map(({ key, label, summary, inverted }) => (
                  <div key={key} className={styles.settingRow}>
                    <div>
                      <div className={styles.settingLabel}>
                        {label}{' '}
                        <Tooltip title={PRODUCT_FLAG_TOOLTIPS[key]} overlayStyle={{ maxWidth: 360 }}>
                          <InfoCircleOutlined style={{ color: '#6D758F', cursor: 'help' }} tabIndex={0} />
                        </Tooltip>
                      </div>
                      <div className={styles.settingSummary}>{summary}</div>
                    </div>
                    <ToggleSwitch
                      checked={inverted ? !formData[key] : !!formData[key]}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          [key]: inverted ? !e.target.checked : e.target.checked,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Ponuda za dodatnu kupovinu (checkout upsell)</h3>
        <p className={styles.fieldLabel} style={{ marginBottom: '16px' }}>
          Samo jedan proizvod može biti aktivna ponuda u isto vreme. Uključivanjem ovde će se
          ponuda automatski isključiti na proizvodu koji je trenutno podešen.
        </p>
        <ToggleSwitch
          checked={formData.checkoutUpsell.enabled}
          onChange={(e) => setCheckoutUpsellField('enabled', e.target.checked)}
          label="Prikaži kao ponudu za dodatnu kupovinu na checkout-u"
          tooltip={PRODUCT_FLAG_TOOLTIPS.checkoutUpsell}
        />
        {formData.checkoutUpsell.enabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '32px', marginTop: '24px' }}>
            <FloatingLabelInput
              label="Dodatni popust (%)"
              type="number"
              value={formData.checkoutUpsell.discountPercent}
              onChange={(e) => setCheckoutUpsellField('discountPercent', e.target.value)}
            />
          </div>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Provizija mentorskog lanca</h3>
        <ToggleSwitch
          checked={formData.mentorCommissionChain.enabled}
          onChange={(e) => setCommissionChainField('enabled', e.target.checked)}
          label="Poseban obračun provizije za ovaj proizvod"
          tooltip={PRODUCT_FLAG_TOOLTIPS.mentorCommissionChain}
        />
        {formData.mentorCommissionChain.enabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '32px', marginTop: '24px' }}>
            <FloatingLabelInput
              label="Maksimalan procenat provizije (%)"
              type="number"
              value={formData.mentorCommissionChain.maxPercent}
              onChange={(e) => setCommissionChainField('maxPercent', e.target.value)}
            />
            {COMMISSION_CHAIN_LEVELS.map(({ key, label }) => (
              <FloatingLabelInput
                key={key}
                label={label}
                type="number"
                value={formData.mentorCommissionChain[key]}
                onChange={(e) => setCommissionChainField(key, e.target.value)}
              />
            ))}
          </div>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Provizija agenta</h3>
        <ToggleSwitch
          checked={formData.agentCommission.enabled}
          onChange={(e) => setAgentCommissionField('enabled', e.target.checked)}
          label="Poseban procenat provizije za agenta na ovom proizvodu"
          tooltip={PRODUCT_FLAG_TOOLTIPS.agentCommission}
        />
        {formData.agentCommission.enabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '32px', marginTop: '24px' }}>
            <FloatingLabelInput
              label="Procenat provizije za agenta (%)"
              type="number"
              value={formData.agentCommission.percent}
              onChange={(e) => setAgentCommissionField('percent', e.target.value)}
            />
          </div>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Cashback za ovaj proizvod</h3>
        <ToggleSwitch
          checked={formData.cashbackOverride.hasOverride}
          onChange={(e) => setCashbackOverrideField('hasOverride', e.target.checked)}
          label="Poseban cashback za ovaj proizvod"
          tooltip="Kada je uključeno, ovaj proizvod koristi sopstveni procenat i način cashback-a umesto opšteg podešavanja iz Lojalnost → Podešavanja."
        />
        {formData.cashbackOverride.hasOverride && (
          <>
            <div style={{ marginTop: '16px' }}>
              <ToggleSwitch
                checked={formData.cashbackOverride.enabled}
                onChange={(e) => setCashbackOverrideField('enabled', e.target.checked)}
                label="Cashback aktivan za ovaj proizvod"
                tooltip="Kada je isključeno, ovaj proizvod ne donosi cashback poene niti odbitak na checkout-u, bez obzira na opšte podešavanje — ne vraća se na opšti procenat."
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '32px', marginTop: '24px' }}>
              <div>
                <FloatingLabelSelect
                  label="Način cashback-a"
                  items={CASHBACK_MODE_OPTIONS}
                  value={formData.cashbackOverride.mode}
                  onChange={(val) => setCashbackOverrideField('mode', val)}
                />
                <p className={styles.fieldLabel} style={{ marginTop: '8px' }}>
                  Poeni: cashback se dodaje kao loyalty poeni posle porudžbine. Odbitak od
                  ukupnog iznosa: cashback se odmah umanjuje od iznosa porudžbine na checkout-u.
                </p>
              </div>
              <FloatingLabelInput
                label="Procenat cashback-a (%)"
                type="number"
                value={formData.cashbackOverride.percent}
                onChange={(e) => setCashbackOverrideField('percent', e.target.value)}
              />
            </div>
          </>
        )}
      </section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
        <Button className="btn-primary" type="primary" onClick={handleSubmit} loading={saving}>
          {mode === 'create' ? 'Kreiraj proizvod' : 'Sačuvaj izmene'}
        </Button>
      </div>
    </div>
  );
}
